/**
 * Centralized Notification Sound Manager
 * Prevents multiple simultaneous sounds and manages sound preferences
 */
class NotificationSoundManager {
  static instance;
  static audioRef = null;
  static isPlaying = false;
  static lastSoundTime = 0;
  static SOUND_COOLDOWN = 2000; // 2 seconds between sounds
  static soundEnabled = true;

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!this.instance) {
      this.instance = new NotificationSoundManager();
    }
    return this.instance;
  }

  /**
   * Initialize the sound manager with audio file
   */
  static initialize(audioFile) {
    if (!this.audioRef) {
      this.audioRef = new Audio(audioFile);
      this.audioRef.volume = 0.5;
      this.audioRef.preload = "auto";
    }

    // Load sound preferences from localStorage
    this.soundEnabled =
      localStorage.getItem("notification_sound_enabled") !== "false";
  }

  /**
   * Play notification sound with proper checks
   */
  static playSound() {
    // Check if sound is enabled
    if (!this.soundEnabled) {
      return;
    }

    // Check if page is hidden (user not active)
    if (document.hidden) {
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now - this.lastSoundTime < this.SOUND_COOLDOWN) {
      return;
    }

    // Check if already playing
    if (this.isPlaying) {
      return;
    }

    // Play the sound
    if (this.audioRef) {
      this.isPlaying = true;
      this.lastSoundTime = now;

      this.audioRef
        .play()
        .catch((error) => {
          // Audio play failed silently
        })
        .finally(() => {
          // Reset playing flag after sound duration
          setTimeout(() => {
            this.isPlaying = false;
          }, 1000);
        });
    } else {
      console.warn("[NotificationSoundManager] Audio not initialized");
    }
  }

  /**
   * Enable/disable notification sounds
   */
  static setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    localStorage.setItem("notification_sound_enabled", enabled.toString());
  }

  /**
   * Check if sound is enabled
   */
  static isSoundEnabled() {
    return this.soundEnabled;
  }

  /**
   * Reset the sound manager (useful for testing)
   */
  static reset() {
    this.isPlaying = false;
    this.lastSoundTime = 0;
  }
}

export default NotificationSoundManager;
